/**
 * Results Editing API
 *
 * Groups query result edits by source table and routes them through
 * the existing table explorer IPC (applyTableExplorerChanges).
 */

import type { PendingUpdate } from '../../core/types/workspaceTypes';
import type { EditableColumnInfo, EditableTableInfo } from '../utils/editabilityDetection';
import { applyTableExplorerChanges } from '../../table-explorer/services/tableExplorerApi';

type ApplyResultsEditsParams = {
  connectionId: string;
  database: string;
  pendingChanges: PendingUpdate[];
  editableColumns: Map<string, EditableColumnInfo>;
  editableTables: Map<string, EditableTableInfo>;
  /** Pass tabId to route through the tab's active transaction session. */
  tabId?: string;
  /**
   * The tab's claim about its transaction (state `active`/`error` → expected;
   * plus the session generation it began). The backend refuses the Save with
   * `TRANSACTION_SESSION_MISSING` when it holds no such session, instead of
   * committing the edit in auto-commit.
   */
  transactionSessionExpected?: boolean;
  transactionSessionGeneration?: number;
};

type ApplyResultsEditsResult = {
  success: boolean;
  error?: string;
  /** First machine-readable refusal seen (`TRANSACTION_SESSION_MISSING`), if any. */
  code?: string;
  /** Number of tables successfully updated */
  tablesUpdated: number;
  /**
   * Identity of each table an UPDATE actually landed on. The caller needs it
   * to refresh anything derived from that table's rows (lookup enum caches —
   * `useLookupCacheWriteThrough`); grouping already happens here, so returning
   * it beats re-deriving it from `editableColumns` at the call site.
   */
  updatedTables: { schema: string; table: string }[];
};

/**
 * Group pending updates by source table and apply each group through
 * the table explorer apply IPC.
 */
export async function applyResultsEdits({
  connectionId,
  database,
  pendingChanges,
  editableColumns,
  editableTables,
  tabId,
  transactionSessionExpected,
  transactionSessionGeneration,
}: ApplyResultsEditsParams): Promise<ApplyResultsEditsResult> {
  if (pendingChanges.length === 0) {
    return { success: true, tablesUpdated: 0, updatedTables: [] };
  }

  // Group changes by source table
  const changesByTable = new Map<string, PendingUpdate[]>();

  for (const change of pendingChanges) {
    const colInfo = editableColumns.get(change.field);
    if (!colInfo) continue;

    const tableKey = `${colInfo.schema}.${colInfo.table}`;
    const existing = changesByTable.get(tableKey) || [];

    // Remap the field name to the source column name (handles aliased columns)
    const remappedChange: PendingUpdate = {
      ...change,
      field: colInfo.sourceColumn,
    };

    existing.push(remappedChange);
    changesByTable.set(tableKey, existing);
  }

  if (changesByTable.size === 0) {
    return {
      success: false,
      error: 'No editable changes to apply',
      tablesUpdated: 0,
      updatedTables: [],
    };
  }

  // Apply changes per table
  const errors: string[] = [];
  const updatedTables: { schema: string; table: string }[] = [];
  let tablesUpdated = 0;
  let code: string | undefined;

  for (const [tableKey, changes] of changesByTable) {
    const tableInfo = editableTables.get(tableKey);
    if (!tableInfo) {
      errors.push(`Table ${tableKey} is not editable`);
      continue;
    }

    const executionId = `results-edit-${tableKey}-${Date.now()}`;

    const result = await applyTableExplorerChanges({
      connectionId,
      executionId,
      table: {
        database,
        schema: tableInfo.schema,
        tableName: tableInfo.table,
      },
      changes,
      tabId,
      ...(transactionSessionExpected ? { transactionSessionExpected: true } : {}),
      ...(transactionSessionGeneration !== undefined ? { transactionSessionGeneration } : {}),
    });

    if (result.success) {
      tablesUpdated++;
      updatedTables.push({ schema: tableInfo.schema, table: tableInfo.table });
    } else {
      errors.push(`${tableKey}: ${result.error || 'Unknown error'}`);
      code ??= result.code;
    }
  }

  if (errors.length > 0) {
    return {
      success: tablesUpdated > 0,
      error: errors.join('\n'),
      ...(code ? { code } : {}),
      tablesUpdated,
      updatedTables,
    };
  }

  return { success: true, tablesUpdated, updatedTables };
}
